import React, { useMemo, useCallback } from 'react';
import { useStore } from 'zustand';
import { useTheme, alpha } from '@mui/material';
import { useUnityBuildStore } from '../../stores/store';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useDragManager } from './DragManager';
import {
  calculatePolygonArea,
  calculateRealWorldDistance,
  calculateRealWorldArea,
} from './takeoffHelper';
import { normalizeColor } from '../../utils/colorUtils';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';

/**
 * Area Polygon Renderer for All Filtered Assemblies
 *
 * Renders filled polygons for all area-type assemblies in filteredCountedAssemblies,
 * not just the current takeoff assembly. This ensures area assemblies are properly
 * visualized with fills and measurements even when they're not actively selected.
 */
export const AreaPolygonRenderer: React.FC = () => {
  const theme = useTheme();
  const { userScale } = useStore(useUnityBuildStore);
  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();
  const { currentTakeoffAssembly } = useStore(useAssemblyStore);
  const { isDragging: isDraggingPoints, getPointPosition } = useDragManager();

  // Get visual position for a point (accounting for drag operations)
  const getVisualPointPosition = useCallback(
    (point: any) => {
      if (isDraggingPoints && point.pointid) {
        const dragPosition = getPointPosition(point.pointid);
        if (dragPosition) {
          return { x: dragPosition.x, y: dragPosition.y };
        }
      }
      return { x: point.x, y: point.y };
    },
    [isDraggingPoints, getPointPosition]
  );

  // Calculate area measurements for a set of points
  const calculateAreaMeasurements = useCallback(
    (points: any[]) => {
      if (points.length < 3) return { area: 0, perimeter: 0 };

      const visualPoints = points.map(getVisualPointPosition);

      // Calculate area using existing helper
      const pixelArea = calculatePolygonArea(visualPoints);
      const realWorldArea = calculateRealWorldArea(pixelArea, userScale);

      // Calculate perimeter
      let perimeter = 0;
      for (let i = 0; i < visualPoints.length; i++) {
        const current = visualPoints[i];
        const next = visualPoints[(i + 1) % visualPoints.length];
        const dx = next.x - current.x;
        const dy = next.y - current.y;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);
        perimeter += segmentLength;
      }
      const realWorldPerimeter = calculateRealWorldDistance(perimeter, userScale);

      return {
        area: realWorldArea,
        perimeter: realWorldPerimeter,
      };
    },
    [getVisualPointPosition, userScale]
  );

  // Render area polygons for all filtered assemblies
  const areaPolygons = useMemo(() => {
    if (
      !filteredCountedAssemblies ||
      Array.isArray(filteredCountedAssemblies) ||
      !filteredCountedAssemblies.filteredAssemblies
    )
      return null;

    const allAssemblies = filteredCountedAssemblies.filteredAssemblies.flatMap(
      (group: any) => group.assembliesList || []
    );

    // Check if currentTakeoffAssembly exists but doesn't have a matching assembly in filteredCountedAssemblies
    // This happens when selecting from FolderNodes (new assembly creation)
    const hasMatchingAssembly =
      currentTakeoffAssembly &&
      allAssemblies.some(
        (assembly: any) => assembly.assemblyid === currentTakeoffAssembly.assemblyid
      );

    // Always include existing filtered assemblies, and add currentTakeoffAssembly if it's new
    const assembliesToRender = hasMatchingAssembly
      ? allAssemblies
      : currentTakeoffAssembly
        ? [...allAssemblies, currentTakeoffAssembly] // Show both existing and new assembly
        : allAssemblies;

    return assembliesToRender
      .filter((assembly: any) => assembly.assemblymeasurementtype === 'area')
      .map((assembly: any) => {
        const isCurrentAssembly = assembly.assemblyid === currentTakeoffAssembly?.assemblyid;

        // UNIFIED STATE: Use filteredCountedAssemblies as base, overlay currentTakeoffAssembly changes
        let effectiveAssembly = assembly;
        let pointsToRender = assembly.pointsList || [];

        // Apply currentTakeoffAssembly overlay only when actively editing
        if (isCurrentAssembly && currentTakeoffAssembly) {
          effectiveAssembly = { ...assembly, ...currentTakeoffAssembly };
          pointsToRender = currentTakeoffAssembly.pointsList || assembly.pointsList || [];
        }

        if (!pointsToRender || pointsToRender.length < 3) return null;

        const visualPoints = pointsToRender.map(getVisualPointPosition);
        const measurements = calculateAreaMeasurements(pointsToRender);

        // Create path data for the polygon
        const pathData =
          visualPoints
            .map((point: any, index: number) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
            .join(' ') + ' Z';

        // Professional color scheme using effective assembly
        const baseColor = normalizeColor(effectiveAssembly.linecolor, theme.palette.primary.main);
        const fillColor = normalizeColor(
          effectiveAssembly.pointfillcolor,
          theme.palette.primary.light
        );

        const colors = {
          stroke: baseColor,
          fill: alpha(fillColor, isCurrentAssembly ? 0.15 : 0.08), // Less opacity for non-current assemblies
          strokeWidth: effectiveAssembly.linesize || 2,
          dashArray: effectiveAssembly.linedasharray || '',
          measurements: theme.palette.text.primary,
          measurementsBg: alpha(theme.palette.background.paper, 0.9),
        };

        // Calculate centroid for measurement label placement
        let centroidX = 0;
        let centroidY = 0;
        visualPoints.forEach((point: any) => {
          centroidX += point.x;
          centroidY += point.y;
        });
        centroidX /= visualPoints.length;
        centroidY /= visualPoints.length;

        return (
          <g key={`area-polygon-${assembly.assemblyid}`}>
            {/* Main filled polygon */}
            <path
              d={pathData}
              fill={colors.fill}
              stroke={colors.stroke}
              strokeWidth={colors.strokeWidth}
              strokeDasharray={colors.dashArray}
              strokeLinejoin="round"
              strokeLinecap="round"
              style={{
                transition: 'fill 0.2s ease-in-out',
              }}
            />

            {/* Area measurement label - only show for significant areas */}
            {measurements.area > 1 && (
              <g>
                {/* Background for better readability */}
                <rect
                  x={centroidX - 35}
                  y={centroidY - 20}
                  width={70}
                  height={25}
                  fill={colors.measurementsBg}
                  stroke={colors.stroke}
                  strokeWidth={0.5}
                  rx={3}
                  ry={3}
                  opacity={isCurrentAssembly ? 1 : 0.8}
                />

                {/* Area text */}
                <text
                  x={centroidX}
                  y={centroidY - 5}
                  textAnchor="middle"
                  fill={colors.measurements}
                  fontSize={isCurrentAssembly ? '11' : '10'}
                  fontWeight="600"
                  fontFamily="monospace"
                  opacity={isCurrentAssembly ? 1 : 0.8}
                >
                  {measurements.area.toFixed(1)} ft²
                </text>
              </g>
            )}

            {/* Enhanced styling for current assembly */}
            {isCurrentAssembly && (
              <path
                d={pathData}
                fill="none"
                stroke={theme.palette.secondary.main}
                strokeWidth={1}
                strokeDasharray="3,3"
                opacity={0.6}
                pointerEvents="none"
              />
            )}
          </g>
        );
      })
      .filter(Boolean); // Remove null entries
  }, [
    filteredCountedAssemblies,
    currentTakeoffAssembly,
    getVisualPointPosition,
    calculateAreaMeasurements,
    theme,
  ]);

  return <g key="area-polygon-renderer">{areaPolygons}</g>;
};
